import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from '../../icons';
import { Modal, ModalActions, ModalFootNote } from '../../components/shared/Modal/Modal';
import { Button } from '../../components/shared/Button/Button';
import { Input } from '../../components/shared/Input/Input';
import { FormField } from '../../components/shared/FormField/FormField';
import { listDocTypes, type DocTypeDto } from '../../api/approval-doc-type';
import {
  listApprovalBoxes, listApprovalDeptBoxes,
  type ApprovalBoxDto, type ApprovalDeptBoxDto,
} from '../../api/approval-box';
import './ApprovalFormPicker.css';

/**
 * 「결재양식」 — 새 결재의 **첫 걸음** (2026-09-07 · 시안 A1 · 정본 F1 `picker`).
 *
 * ── 무엇을 맡나 ──────────────────────────────────────────────────────────
 * 정본이 적어 둔 것: *「필드가 20개를 넘으면 모달이 아니라 화면을 만든다.」*
 * 서식 칸은 마흔까지 가므로 **기안은 화면**이다(`/approval/new`, 2026-08-31에 옮겼다).
 * 이 창이 맡는 것은 그 앞의 **양식 고르기 + 딸린 칸**뿐이고, 「기안하기」가 그 화면으로 넘긴다.
 *
 * ── ★★★ 전사결재함은 고르는 칸이 아니다 (사용자 지정) ────────────────────
 * 왼쪽에서 고른 **묶음이 그대로** 전사결재함이다 — 「인사 › 휴가신청」이면 인사함.
 * 그래서 오른쪽에 셀렉트가 없고 **읽는 칩**이 선다. 같은 값을 두 자리에서 정하게 하면
 * 언젠가 어긋나고, 어긋나면 「이 문서가 어느 함에 있나」가 화면마다 달라진다.
 *
 * ── ★★★ 보존연한도 고르는 칸이 아니다 (사용자 지정) ──────────────────────
 * **회사 관리자가** 「양식 · 서식」에서 양식마다 정한다. 같은 휴가신청이 사람마다 3년·10년이면
 * 보존 규정 자체가 성립하지 않는다. 그래서 여기서는 읽기만 한다.
 *
 * ── ★★★ 기안부서 · 부서 결재함의 기본은 「미지정」 (사용자 지정) ──────────
 * 부서가 하나뿐이고 부서 결재함이 아직 없으면 **고를 것 자체가 없다.** 그때 소속을 자동으로
 * 채우면 «정해진 값»처럼 보이지만 실은 아무것도 안 정한 것이다.
 * ★ 「미지정」은 «비활성»과 다르다 — 회색이되 **화살표를 안 그린다**(누를 목록이 없다).
 *   그리고 **왜** 미지정인지를 아래 한 줄로 적는다. 이유 없이 회색이면 권한 문제로 읽힌다.
 *
 * ── ★★ 제목은 미리 차 있다 (사용자 지정) ────────────────────────────────
 * 양식에 `defaultTitle` 이 있으면 그것을, 없으면 **양식 이름**을 넣는다. 그대로 올려도 되고
 * 고쳐도 된다 — 빈 칸으로 두면 「휴가신청」이라고 칠 사람이 대부분인데 그 한 번을 매번 시킨다.
 */

/** 회사가 안 정했을 때의 보존연한 — 서버 `DEFAULT_RETENTION_YEARS` 와 같은 값. */
const DEFAULT_RETENTION_YEARS = 5;

/** 묶음이 없는 양식들이 모이는 자리. **지어낸 함이 아니라 「아직 안 정했다」는 뜻이다.** */
const NO_BOX = '__none__';

interface Props {
  open: boolean;
  onClose: () => void;
}

export function ApprovalFormPicker({ open, onClose }: Props): JSX.Element | null {
  const navigate = useNavigate();

  const [types, setTypes] = useState<DocTypeDto[] | null>(null);
  const [boxes, setBoxes] = useState<ApprovalBoxDto[]>([]);
  const [deptBoxes, setDeptBoxes] = useState<ApprovalDeptBoxDto[]>([]);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  /** 고른 부서 결재함. 열린 함이 없으면 고를 것이 없어 그대로 `null` 이다. */
  const [deptBoxId, setDeptBoxId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    // 조각마다 따로 — 결재함을 못 읽어도 양식 목록은 뜬다(묶음만 「묶음 없음」이 된다).
    void listDocTypes()
      .then((t) => { if (alive) setTypes(t); })
      .catch(() => { if (alive) setTypes([]); });
    void listApprovalBoxes()
      .then((b) => { if (alive) setBoxes(b); })
      .catch(() => { if (alive) setBoxes([]); });
    void listApprovalDeptBoxes()
      .then((d) => { if (alive) setDeptBoxes(d); })
      .catch(() => { if (alive) setDeptBoxes([]); });
    return () => { alive = false; };
  }, [open]);

  // 창을 닫으면 고른 것을 놓는다 — 다음에 열었을 때 남의 선택이 남아 있으면 안 된다.
  useEffect(() => {
    if (open) return;
    setPicked(null); setTitle(''); setQuery(''); setDeptBoxId(null);
  }, [open]);

  const boxByCode = useMemo(() => new Map(boxes.map((b) => [b.code, b])), [boxes]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = types ?? [];
    return q ? list.filter((t) => t.name.toLowerCase().includes(q)) : list;
  }, [types, query]);

  /**
   * 묶음별로 가른다 — 이 목록이 곧 전사결재함이다.
   *
   * ★★ **빈 결재함은 그리지 않는다.** 씨앗 일곱 중 양식이 붙은 것만 선다 — 빈 묶음을 세우면
   *    「여기 뭔가 있어야 하는데 없다」로 읽힌다. 일곱을 다 보는 것은 관리 화면의 일이다.
   */
  const groups = useMemo(() => {
    const bucket = new Map<string, DocTypeDto[]>();
    for (const t of shown) {
      const key = t.boxCode ?? NO_BOX;
      const arr = bucket.get(key);
      if (arr) arr.push(t); else bucket.set(key, [t]);
    }
    const ordered = [...bucket.entries()].sort((a, b) => {
      if (a[0] === NO_BOX) return 1;
      if (b[0] === NO_BOX) return -1;
      return (boxByCode.get(a[0])?.sortOrder ?? 999) - (boxByCode.get(b[0])?.sortOrder ?? 999);
    });
    return ordered.map(([code, items]) => ({
      code,
      // 회사가 지은 이름을 쓴다. 함을 못 읽었으면 코드를 그대로 — 지어내지 않는다.
      label: code === NO_BOX ? '묶음 없음' : (boxByCode.get(code)?.name ?? code),
      items,
    }));
  }, [shown, boxByCode]);

  const pickedType = useMemo(
    () => (types ?? []).find((t) => t.code === picked) ?? null,
    [types, picked],
  );
  const pickedBoxName = pickedType?.boxCode ? (boxByCode.get(pickedType.boxCode)?.name ?? pickedType.boxCode) : null;
  const retention = pickedType?.retentionYears ?? DEFAULT_RETENTION_YEARS;

  /** 양식을 고르면 제목을 채운다 — 사람이 이미 고쳐 놓았으면 덮지 않는다. */
  const pick = (t: DocTypeDto, prevAuto: string) => {
    setPicked(t.code);
    const next = t.defaultTitle?.trim() || t.name;
    setTitle((cur) => (cur.trim() === '' || cur === prevAuto ? next : cur));
  };
  const autoTitle = pickedType ? (pickedType.defaultTitle?.trim() || pickedType.name) : '';

  const go = () => {
    if (!pickedType) return;
    /**
     * ★ 기안 화면으로 **값을 들고** 간다. 주소에 싣지 않고 라우터 state 로 넘긴다 —
     *   제목에 «/» 나 «?» 가 들어가는 일이 흔하고, 주소에 실으면 그때 깨진다.
     */
    navigate('/approval/new', {
      state: { type: pickedType.code, title: title.trim() || autoTitle, deptBoxId },
    });
    onClose();
  };

  if (!open) return null;

  return (
    <Modal
      title="결재양식"
      size="md"
      bodyFlat
      onClose={onClose}
      footer={(
        <>
          <ModalFootNote>「기안하기」를 누르면 기안 화면으로 넘어갑니다.</ModalFootNote>
          <ModalActions>
            <Button variant="secondary" size="lg" onClick={onClose}>취소</Button>
            {/* ★ 고르기 전에는 안 눌린다 — 무엇의 제목인지 모르는 채로 제목부터 치게 하지 않는다. */}
            <Button variant="primary" size="lg" onClick={go} disabled={!pickedType}>기안하기</Button>
          </ModalActions>
        </>
      )}
    >
      <div className="apick">
        {/* ── 좌 240 — 결재함으로 묶은 양식 ── */}
        <div className="apick__pick">
          <div className="apick__srch">
            <Input
              size="sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="양식 이름으로 찾기"
              aria-label="양식 이름으로 찾기"
              prefix={<Search size={14} aria-hidden />}
              fullWidth
            />
          </div>

          <div className="apick__list scrollbar-thin" role="listbox" aria-label="결재 양식">
            {types === null && <p className="apick__none">불러오는 중…</p>}
            {types !== null && groups.length === 0 && (
              <p className="apick__none">
                {query.trim() ? '찾는 양식이 없습니다.' : '회사에 열린 양식이 없습니다.'}
              </p>
            )}
            {groups.map((g) => (
              <div key={g.code}>
                <div className="apick__g">{g.label}<i /></div>
                {g.items.map((t) => (
                  <button
                    key={t.id ?? t.code}
                    type="button"
                    role="option"
                    aria-selected={picked === t.code}
                    className={`apick__f${picked === t.code ? ' is-on' : ''}`}
                    onClick={() => pick(t, autoTitle)}
                  >
                    <span className="apick__fnm">{t.name}</span>
                    <span className="apick__cnt">{t.defaultSteps}단계</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* ── 우 480 — 고른 것과 그에 딸린 칸 ── */}
        <div className="apick__detail">
          {!pickedType ? (
            <p className="apick__empty">왼쪽에서 양식을 고르세요.</p>
          ) : (
            <>
              {/* 읽는 자리 셋 — 양식 이름 · 전사결재함 · 보존연한 */}
              <div className="apick__picked">
                <b>{pickedType.name}</b>
                {pickedBoxName && (
                  <span className="apick__chip apick__chip--box">전사결재함 <b>{pickedBoxName}</b></span>
                )}
                <span className="apick__chip">보존연한 <b>{retention}년</b></span>
              </div>

              {/* 유일하게 «치는» 칸 */}
              <FormField label="제목" required>
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={500}
                  size="lg"
                  fullWidth
                  autoFocus
                  placeholder={autoTitle}
                />
              </FormField>

              <div className="apick__two">
                <FormField label="기안부서">
                  {/* ★ 부서를 «고르는» 것은 다음 판이다 — 지금은 열린 선택지가 없다. */}
                  <div className="apick__none-in">미지정</div>
                </FormField>
                <FormField label="부서 결재함">
                  {deptBoxes.length === 0 ? (
                    <div className="apick__none-in">미지정</div>
                  ) : (
                    <select
                      className="apick__sel"
                      value={deptBoxId ?? ''}
                      onChange={(e) => setDeptBoxId(e.target.value || null)}
                      aria-label="부서 결재함"
                    >
                      <option value="">미지정</option>
                      {deptBoxes.map((d) => (
                        <option key={d.id} value={d.id}>{d.name}</option>
                      ))}
                    </select>
                  )}
                </FormField>
              </div>

              <p className="apick__why">
                제목은 <b>양식 이름</b>으로 미리 채웠습니다 — 그대로 올려도 되고 고쳐도 됩니다.
                {deptBoxes.length === 0 && (
                  <>
                    <br />
                    부서 결재함이 <b>아직 열려 있지 않아</b> 지정할 것이 없습니다 —
                    회사 관리자가 부서 결재함을 열면 그때 고릅니다.
                  </>
                )}
              </p>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
